import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Minus,
  Plus,
  X,
  ShoppingBag,
  MessageCircle,
  ShieldCheck,
  Lock,
  Headphones,
  Sparkles,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ProductCard } from "../components/vanities/ProductCard";
import { SmartImage } from "../components/ui/SmartImage";
import { toast } from "../components/ui/sonner";
import { useCart } from "../context/CartContext";
import { useProducts } from "../lib/useApi";
import { createCheckoutSession } from "../lib/api";
import { formatPrice } from "../lib/filters";
import { WHATSAPP_URL } from "../lib/site";

const STEPS = ["Cart", "Checkout", "Payment"];

const StepCue = () => (
  <div data-testid="cart-steps" className="flex items-center gap-3 text-xs">
    {STEPS.map((s, i) => (
      <div key={s} className="flex items-center gap-3">
        <span className={`inline-flex items-center gap-2 ${i === 0 ? "text-charcoal font-medium" : "text-taupe"}`}>
          <span className={`grid place-items-center h-5 w-5 rounded-full text-[0.62rem] ${i === 0 ? "bg-charcoal text-ivory" : "border border-beige text-taupe"}`}>
            {i + 1}
          </span>
          {s}
        </span>
        {i < STEPS.length - 1 && <span className="h-px w-6 bg-beige" />}
      </div>
    ))}
  </div>
);

const TRUST = [
  { icon: Lock, t: "Secure checkout" },
  { icon: ShieldCheck, t: "Transparent pricing" },
  { icon: Headphones, t: "Support before purchase" },
  { icon: Sparkles, t: "Premium curated products" },
];

const QtyStepper = ({ item, updateQty }) => (
  <div className="inline-flex items-center border border-beige rounded-full">
    <button
      onClick={() => updateQty(item.lid, item.qty - 1)}
      data-testid={`qty-dec-${item.lid}`}
      aria-label="Decrease quantity"
      className="grid place-items-center h-9 w-9 text-charcoal hover:text-taupe transition-colors disabled:opacity-30"
      disabled={item.qty <= 1}
    >
      <Minus size={14} strokeWidth={2} />
    </button>
    <span data-testid={`qty-value-${item.lid}`} className="w-8 text-center text-sm tabular-nums">
      {item.qty}
    </span>
    <button
      onClick={() => updateQty(item.lid, item.qty + 1)}
      data-testid={`qty-inc-${item.lid}`}
      aria-label="Increase quantity"
      className="grid place-items-center h-9 w-9 text-charcoal hover:text-taupe transition-colors"
    >
      <Plus size={14} strokeWidth={2} />
    </button>
  </div>
);

const CartItem = ({ item, removeItem, updateQty }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`cart-item-${item.lid}`}
    className="flex gap-5 md:gap-7 py-8 border-b border-beige"
  >
    <Link to={`/vanities/${item.id}`} className="shrink-0 w-28 md:w-36 overflow-hidden rounded-sm bg-stone spotlight">
      <SmartImage src={item.image} alt={item.name} label={item.name} className="w-full aspect-[4/5] object-cover" />
    </Link>

    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-serif text-xl md:text-2xl text-charcoal leading-tight">
          <Link to={`/vanities/${item.id}`} className="hover:text-taupe transition-colors">
            {item.name}
          </Link>
        </h3>
        <p className="font-serif text-lg md:text-xl text-charcoal whitespace-nowrap">
          {formatPrice(item.price * item.qty)}
        </p>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {[item.mounting, `${item.width}" Width`, item.sink, item.finish].map((m) => (
          <span key={m} className="text-[0.68rem] text-charcoal/70 bg-stone/70 border border-beige/70 px-2 py-1 rounded-full">
            {m}
          </span>
        ))}
      </div>

      {/* Confidence note */}
      <p className="mt-4 text-xs text-charcoal/55 leading-relaxed">
        Need help confirming the size?{" "}
        <a
          href={WHATSAPP_URL(`Hi BathLane, I'd like to confirm the size of the ${item.name} before checkout.`)}
          target="_blank"
          rel="noreferrer"
          data-testid={`item-whatsapp-${item.lid}`}
          className="text-charcoal font-medium link-sweep"
        >
          Talk on WhatsApp
        </a>{" "}
        or{" "}
        <Link to={`/vanities/${item.id}`} className="text-charcoal font-medium link-sweep">
          review details
        </Link>
        .
      </p>

      <div className="mt-5 flex items-center justify-between gap-4">
        <QtyStepper item={item} updateQty={updateQty} />
        <div className="flex items-center gap-4">
          <button
            onClick={() => toast("Saved for later", { description: item.name })}
            data-testid={`save-${item.lid}`}
            className="hidden sm:inline text-xs text-taupe link-sweep"
          >
            Save for later
          </button>
          <button
            onClick={() => {
              removeItem(item.lid);
              toast("Removed from cart", { description: item.name });
            }}
            data-testid={`remove-${item.lid}`}
            className="inline-flex items-center gap-1.5 text-xs text-charcoal/60 hover:text-charcoal transition-colors"
          >
            <X size={13} strokeWidth={2} /> Remove
          </button>
        </div>
      </div>
    </div>
  </motion.div>
);

const EmptyCart = () => (
  <div data-testid="cart-empty" className="text-center py-20 flex flex-col items-center">
    <span className="grid place-items-center h-20 w-20 rounded-full bg-stone">
      <ShoppingBag size={30} strokeWidth={1.2} className="text-taupe" />
    </span>
    <h1 className="font-serif font-light text-charcoal text-4xl md:text-5xl mt-8">Your cart is empty</h1>
    <p className="mt-4 max-w-md text-charcoal/65 leading-relaxed">
      Explore our curated wall mounted and floor mounted vanities — chosen for fit,
      finish and everyday calm.
    </p>
    <Link
      to="/vanities"
      data-testid="empty-shop-cta"
      className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory px-8 py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
    >
      Shop Vanities <ArrowRight size={16} strokeWidth={1.5} />
    </Link>
    <div className="mt-6 flex items-center gap-6 text-sm">
      <Link to="/vanities?mount=wall" data-testid="empty-wall" className="text-charcoal link-sweep">Wall Mounted</Link>
      <span className="text-beige">·</span>
      <Link to="/vanities?mount=floor" data-testid="empty-floor" className="text-charcoal link-sweep">Floor Mounted</Link>
    </div>
  </div>
);

const AddonLine = ({ item, removeItem }) => (
  <div
    data-testid={`cart-addon-${item.lid}`}
    className="flex items-center gap-4 py-4 pl-4 md:pl-10 border-b border-beige/60 bg-stone/20"
  >
    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-sm bg-stone">
      <SmartImage src={item.image} alt={item.name} label={item.name} loading="lazy" className="h-full w-full object-cover" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[0.66rem] uppercase tracking-[0.14em] text-taupe">Add-on · {item.category}</p>
      <p className="text-sm font-medium text-charcoal leading-tight truncate">{item.name}</p>
      <p className="text-xs text-taupe">{item.finish}</p>
    </div>
    <p className="font-serif text-base text-charcoal whitespace-nowrap">{formatPrice(item.price)}</p>
    <button
      onClick={() => {
        removeItem(item.lid);
        toast("Add-on removed", { description: item.name });
      }}
      data-testid={`cart-addon-remove-${item.lid}`}
      aria-label="Remove add-on"
      className="text-charcoal/50 hover:text-charcoal transition-colors shrink-0"
    >
      <X size={15} strokeWidth={2} />
    </button>
  </div>
);

const FASTRR_SCRIPT = "https://checkout-ui.shiprocket.com/assets/js/channels/shopify.js";

const LAST_ORDER_KEY = "bathlane_last_oid";

const loadFastrrScript = () =>
  new Promise((resolve) => {
    if (window.HeadlessCheckout) return resolve();
    const s = document.createElement("script");
    s.src = FASTRR_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });

const startCheckout = async (items, onError) => {
  if (items.length === 0) return;
  let settled = false;
  let lockWatch = null;
  let sbwBase = 0;
  let appliedPad = null;
  const fail = () => {
    if (settled) return;
    settled = true;
    unlockScroll();
    lockWatch?.disconnect();
    onError();
  };
  const unlockScroll = () => {
    appliedPad = null;
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  };
  const lockScroll = () => {
    if (document.body.style.overflow !== "hidden") return;
    const pad = sbwBase > 0 ? `${sbwBase}px` : "";
    if (appliedPad !== pad) {
      appliedPad = pad;
      document.body.style.paddingRight = pad;
    }
  };
  try {
    const { token, order_id } = await createCheckoutSession(items);
    if (!token) throw new Error("no_token");
    if (order_id) sessionStorage.setItem(LAST_ORDER_KEY, String(order_id));
    await loadFastrrScript();
    if (!window.HeadlessCheckout) throw new Error("sdk_unavailable");
    for (const id of ["fastrr-main-container", "headless-container"]) {
      document.getElementById(id)?.remove();
    }
    unlockScroll();
    void document.documentElement.offsetWidth;
    sbwBase = window.innerWidth - document.documentElement.clientWidth;
    lockWatch = new MutationObserver(lockScroll);
    lockWatch.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    window.HeadlessCheckout.addToCart(
      {},
      token,
      { fallbackUrl: order_id ? `/order/confirmed?oid=${order_id}` : "/order/confirmed" },
      fail
    );
    lockScroll();
    window.addEventListener(
      "resize",
      () => {
        if (document.body.style.overflow === "hidden") lockScroll();
      },
      { once: true }
    );
    const t0 = Date.now();
    const watchdog = () => {
      if (settled) return;
      if (document.getElementById("headless-modal") || document.getElementById("headless-iframe")) return;
      if (Date.now() - t0 >= 5000) return fail();
      setTimeout(watchdog, 300);
    };
    watchdog();
  } catch {
    fail();
  }
};

export default function Cart() {
  const { items, removeItem, updateQty, subtotal, count } = useCart();
  const hasItems = items.length > 0;
  const catalog = useProducts({ kind: "vanity" });
  const catalogItems = catalog.data?.items ?? [];

  const suggested = catalogItems
    .filter((p) => !items.some((i) => i.id === p.id))
    .slice(0, 4);

  const checkoutFailed = () => {
    toast("Fast checkout unavailable", {
      description: "Review your cart and retry, or use the backup checkout below.",
    });
  };

  const checkout = () => {
    const lines = items.map((i) => ({
      id: i.id,
      name: i.name,
      price: i.price,
      image: i.image || null,
      qty: i.qty,
      type: i.type === "addon" ? "addon" : "vanity",
      width: i.width ?? null,
      finish: i.finish ?? null,
      sink: i.sink ?? null,
      mounting: i.mounting ?? null,
      style: i.style ?? null,
      category: i.category ?? null,
    }));
    startCheckout(lines, checkoutFailed);
  };

  return (
    <div data-testid="cart-page" className="bg-ivory min-h-screen">
      <Header />

      <main className="pt-28 md:pt-32 pb-28 lg:pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {/* Top row */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-12">
            <div>
              <h1 className="font-serif font-light text-charcoal text-4xl md:text-6xl leading-[1.02] tracking-tight">
                Your Cart
              </h1>
              {hasItems && (
                <p data-testid="cart-count-label" className="mt-3 eyebrow text-taupe">
                  {count} {count === 1 ? "item" : "items"}
                </p>
              )}
            </div>
            <StepCue />
          </div>

          {!hasItems ? (
            <EmptyCart />
          ) : (
            <div className="lg:grid lg:grid-cols-[1fr_390px] lg:gap-14">
              {/* Left: items */}
              <section data-testid="cart-items">
                <div className="border-t border-beige">
                  {items
                    .filter((i) => i.type !== "addon")
                    .map((v) => (
                      <div key={v.lid}>
                        <CartItem item={v} removeItem={removeItem} updateQty={updateQty} />
                        {items
                          .filter((a) => a.type === "addon" && a.parentLid === v.lid)
                          .map((a) => (
                            <AddonLine key={a.lid} item={a} removeItem={removeItem} />
                          ))}
                      </div>
                    ))}
                </div>

                <div className="mt-8 flex items-center justify-between">
                  <Link
                    to="/vanities"
                    data-testid="continue-shopping"
                    className="inline-flex items-center gap-2 text-sm font-medium text-charcoal link-sweep"
                  >
                    <ArrowLeft size={15} strokeWidth={1.5} /> Continue shopping
                  </Link>
                </div>

                {/* Support module */}
                <div className="mt-12 bg-stone rounded-sm p-7 md:p-9 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                  <div>
                    <h3 className="font-serif text-2xl text-charcoal">Need help before checkout?</h3>
                    <p className="mt-1.5 text-sm text-charcoal/65">
                      Confirm dimensions, finish or fit with our team before you pay.
                    </p>
                  </div>
                  <a
                    href={WHATSAPP_URL()}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="cart-support-whatsapp"
                    className="shrink-0 inline-flex items-center gap-2 rounded-full border border-charcoal px-6 py-3.5 text-sm font-medium text-charcoal btn-fill hover:text-charcoal transition-colors duration-500"
                  >
                    <MessageCircle size={16} strokeWidth={1.5} /> Talk on WhatsApp
                  </a>
                </div>
              </section>

              {/* Right: sticky summary */}
              <aside data-testid="order-summary" className="mt-14 lg:mt-0">
                <div className="lg:sticky lg:top-28">
                  <div className="border border-beige rounded-sm p-7 md:p-8 bg-ivory">
                    <h2 className="font-serif text-2xl text-charcoal">Order Summary</h2>

                    <div className="mt-6 space-y-3.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-charcoal/70">Subtotal</span>
                        <span data-testid="summary-subtotal" className="text-charcoal font-medium">{formatPrice(subtotal)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-charcoal/70">Dispatch</span>
                        <span className="text-charcoal/70 text-right">Confirmed at checkout</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-taupe">Taxes</span>
                        <span className="text-taupe">Inclusive of all taxes (GST)</span>
                      </div>
                    </div>

                    <div className="mt-6 pt-5 border-t border-beige flex items-center justify-between">
                      <span className="font-serif text-xl text-charcoal">Total</span>
                      <span data-testid="summary-total" className="font-serif text-2xl text-charcoal">{formatPrice(subtotal)}</span>
                    </div>

                    <button
                      onClick={checkout}
                      data-testid="proceed-checkout"
                      className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-charcoal text-ivory py-4 text-sm font-medium btn-fill hover:text-charcoal transition-colors duration-500"
                    >
                      Proceed to Checkout <ArrowRight size={16} strokeWidth={1.5} />
                    </button>

                    <Link
                      to="/vanities"
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-beige py-3.5 text-sm font-medium text-charcoal hover:border-charcoal transition-colors duration-300"
                    >
                      Continue Shopping
                    </Link>

                    <Link
                      to="/checkout"
                      data-testid="backup-checkout-link"
                      className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 text-xs text-taupe hover:text-charcoal transition-colors"
                    >
                      Prefer COD? Complete via backup checkout
                    </Link>
                  </div>

                  {/* Trust strip */}
                  <div data-testid="cart-trust" className="mt-5 grid grid-cols-2 gap-3">
                    {TRUST.map((t) => {
                      const Icon = t.icon;
                      return (
                        <div key={t.t} className="flex items-center gap-2.5 border border-beige rounded-sm px-4 py-3.5">
                          <Icon size={17} strokeWidth={1.4} className="text-charcoal shrink-0" />
                          <span className="text-xs text-charcoal/75 leading-tight">{t.t}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </aside>
            </div>
          )}

          {/* Suggested products */}
          {suggested.length > 0 && (
            <section data-testid="cart-suggested" className="mt-24 md:mt-32">
              <div className="flex items-end justify-between gap-6 mb-12">
                <h2 className="font-serif font-light text-charcoal text-3xl md:text-5xl leading-[1.05] tracking-tight">
                  You may also like
                </h2>
                <Link to="/vanities" className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-charcoal link-sweep whitespace-nowrap">
                  View all <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                {suggested.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />

      {/* Mobile sticky checkout bar */}
      {hasItems && (
        <div
          data-testid="cart-sticky-bar"
          className="fixed bottom-0 inset-x-0 z-50 lg:hidden bg-ivory/97 backdrop-blur-sm border-t border-beige px-4 py-3 flex items-center justify-between gap-4"
        >
          <div>
            <p className="text-[0.68rem] text-taupe leading-none">Total</p>
            <p className="font-serif text-xl text-charcoal leading-tight">{formatPrice(subtotal)}</p>
          </div>
          <button
            onClick={checkout}
            data-testid="cart-sticky-checkout"
            className="inline-flex items-center gap-2 rounded-full bg-charcoal text-ivory px-7 py-3.5 text-sm font-medium"
          >
            Proceed to Checkout <ArrowRight size={15} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}
